import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Users,
  Mail,
  Phone,
  Gift,
  PartyPopper
} from 'lucide-react';
import confetti from 'canvas-confetti';
import AccountCreationPanel from '../components/AccountCreationPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BookingSuccess = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // iter-135 — fetchBooking() will redirect held bookings to /held
    // before we ever show the page. Confetti fires only if we actually
    // stay on this page (booking is confirmed).
    let confettiTriggered = false;
    (async () => {
      const status = await fetchBooking();
      if (status === 'confirmed_or_paid' && !confettiTriggered) {
        confettiTriggered = true;
        launchCelebration();
      }
    })();
  }, [bookingId]);

  const launchCelebration = () => {
    // Respect reduced-motion preference
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }
    } catch { /* ignore */ }

    const BRAND_COLORS = ['#FF3B6B', '#FF7A2E', '#FFD166', '#06D6A0', '#6366F1', '#FFB5D8'];

    // 1. Opening burst from center — big and immediate
    confetti({
      particleCount: 120,
      spread: 90,
      origin: { y: 0.55 },
      colors: BRAND_COLORS,
      scalar: 1.1,
      ticks: 240,
    });

    // 2. Side cannons 200ms later (left + right simultaneous)
    setTimeout(() => {
      confetti({
        particleCount: 60,
        angle: 60,
        spread: 55,
        startVelocity: 55,
        origin: { x: 0, y: 0.75 },
        colors: BRAND_COLORS,
      });
      confetti({
        particleCount: 60,
        angle: 120,
        spread: 55,
        startVelocity: 55,
        origin: { x: 1, y: 0.75 },
        colors: BRAND_COLORS,
      });
    }, 200);

    // 3. School-pride style gentle rain for 1.8s — two streams from off-screen
    const rainEnd = Date.now() + 1800;
    const rainInterval = setInterval(() => {
      if (Date.now() > rainEnd) { clearInterval(rainInterval); return; }
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 50,
        startVelocity: 45,
        origin: { x: 0, y: 0.6 },
        colors: BRAND_COLORS,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 50,
        startVelocity: 45,
        origin: { x: 1, y: 0.6 },
        colors: BRAND_COLORS,
      });
    }, 120);
  };

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bookings/${bookingId}`);
      // iter-135 — if the booking is still on a Pay-Later hold (not paid),
      // redirect to the Booking-Held page so the customer sees the correct
      // "still need to pay" copy instead of "confirmed" + confetti.
      if (response.data?.status === 'pending_hold') {
        navigate(`/booking/${bookingId}/held`, { replace: true });
        return 'redirected';
      }
      setBooking(response.data);
      return 'confirmed_or_paid';
    } catch (error) {
      console.error('Failed to load booking');
      return 'error';
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF8]">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />

      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#06D6A0]/20 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-[#06D6A0]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#1E1E24] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Booking Confirmed!
          </h1>
          <p className="text-[#6B6B7A] text-lg">
            Get ready to celebrate {booking?.child_name}'s birthday!
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden"
        >
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-[#FF3B6B] to-[#E62E58] p-6 text-white">
            <div className="flex items-center gap-3">
              <PartyPopper className="w-8 h-8" />
              <div>
                <p className="font-semibold text-lg">{booking?.package_name}</p>
                <p className="text-white/80 text-sm" data-testid="success-booking-reference">
                  Booking Reference: <span className="font-mono font-bold tracking-wider">{booking?.booking_reference || booking?.booking_id}</span>
                </p>
                {booking?.is_pay_later && booking?.payment_due_date && (
                  <p className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FFD166] text-[#1E1E24] text-xs font-semibold" data-testid="success-pay-later-badge">
                    Pay Later — due by {booking.payment_due_date}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="p-6 sm:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#FF3B6B]/10 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-[#FF3B6B]" />
                </div>
                <div>
                  <p className="text-sm text-[#6B6B7A]">Date</p>
                  <p className="font-semibold text-[#1E1E24]">
                    {new Date(booking?.date).toLocaleDateString('en-CA', { 
                      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#06D6A0]/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#06D6A0]" />
                </div>
                <div>
                  <p className="text-sm text-[#6B6B7A]">Time</p>
                  <p className="font-semibold text-[#1E1E24]">
                    {booking?.time_slot === '10:00' ? '10:00 AM' : booking?.time_slot === '13:00' ? '1:00 PM' : '4:00 PM'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#FFD166]/20 flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-[#B8860B]" />
                </div>
                <div>
                  <p className="text-sm text-[#6B6B7A]">Birthday Child</p>
                  <p className="font-semibold text-[#1E1E24]">
                    {booking?.child_name} ({booking?.child_age} years old)
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#FF3B6B]/10 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-[#FF3B6B]" />
                </div>
                <div>
                  <p className="text-sm text-[#6B6B7A]">Guests</p>
                  <p className="font-semibold text-[#1E1E24]">{booking?.guest_count} guests</p>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-[#FFFDF8] rounded-xl p-4 mb-6">
              <p className="text-sm font-semibold text-[#1E1E24] mb-3">Contact Information</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-[#6B6B7A]" />
                  <span className="text-sm text-[#1E1E24]">{booking?.customer_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#6B6B7A]" />
                  <span className="text-sm text-[#1E1E24]">{booking?.customer_email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#6B6B7A]" />
                  <span className="text-sm text-[#1E1E24]">{booking?.customer_phone}</span>
                </div>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="border-t border-[#E8E5DF] pt-6">
              <p className="text-sm font-semibold text-[#1E1E24] mb-4">Payment Summary</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B6B7A]">Package</span>
                  <span className="text-[#1E1E24]">${booking?.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B6B7A]">Tax (13%)</span>
                  <span className="text-[#1E1E24]">${booking?.tax?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold pt-2 border-t border-[#E8E5DF]">
                  <span className="text-[#1E1E24]">Total</span>
                  <span className="text-[#1E1E24]">${booking?.total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mt-4">
                  <span className="text-[#6B6B7A]">Payment Status</span>
                  <span className={`font-medium ${
                    booking?.payment_status === 'fully_paid' 
                      ? 'text-[#06D6A0]' 
                      : booking?.payment_status === 'deposit_paid'
                      ? 'text-[#FFD166]'
                      : 'text-[#FF3B6B]'
                  }`}>
                    {booking?.payment_status === 'fully_paid' 
                      ? 'Fully Paid' 
                      : booking?.payment_status === 'deposit_paid'
                      ? 'Deposit Paid'
                      : 'Pending'
                    }
                  </span>
                </div>
                {booking?.payment_status === 'deposit_paid' && (
                  <div className="flex justify-between text-sm bg-[#FFD166]/10 p-3 rounded-xl mt-2">
                    <span className="text-[#B8860B]">Balance Due (7 days before party)</span>
                    <span className="text-[#B8860B] font-semibold">
                      ${(booking?.total - booking?.deposit_amount).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-[#FFFDF8] p-6 border-t border-[#E8E5DF]">
            <div className="text-center">
              <p className="text-sm text-[#6B6B7A] mb-4">
                A confirmation email has been sent to {booking?.customer_email}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/my-bookings')}
                  variant="outline"
                  className="rounded-full border-[#E8E5DF]"
                  data-testid="view-bookings-btn"
                >
                  View My Bookings
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full btn-hover"
                  data-testid="back-home-btn"
                >
                  Back to Home
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Post-booking: offer account creation (hidden if already logged in) */}
        <AccountCreationPanel booking={booking} />

        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 bg-white rounded-2xl border border-[#E8E5DF] p-6"
        >
          <h3 className="text-lg font-semibold text-[#1E1E24] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            What's Next?
          </h3>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#06D6A0] flex items-center justify-center flex-shrink-0 text-white font-semibold">
                1
              </div>
              <div>
                <p className="font-medium text-[#1E1E24]">Confirmation Email</p>
                <p className="text-sm text-[#6B6B7A]">You'll receive a detailed confirmation email shortly</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#FFD166] flex items-center justify-center flex-shrink-0 text-white font-semibold">
                2
              </div>
              <div>
                <p className="font-medium text-[#1E1E24]">7 Days Before</p>
                <p className="text-sm text-[#6B6B7A]">We'll send a reminder with party details and balance payment (if applicable)</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#FF3B6B] flex items-center justify-center flex-shrink-0 text-white font-semibold">
                3
              </div>
              <div>
                <p className="font-medium text-[#1E1E24]">Party Day!</p>
                <p className="text-sm text-[#6B6B7A]">Arrive 15 minutes early and let the celebration begin!</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BookingSuccess;
